/**
 * Location Intelligence Agent
 * Analyzes location data, calculates optimal visiting routes, identifies farm clusters
 */

interface Location {
  lat: number;
  lng: number;
  address?: string;
}

interface Farm {
  id: string;
  name: string;
  location: Location;
  distance?: number;
}

interface Route {
  farms: Farm[];
  totalDistance: number;
  estimatedTime: number;
  efficiency: number;
}

interface FarmCluster {
  clusterId: string;
  farms: Farm[];
  centerLocation: Location;
  radius: number;
  density: number;
}

interface GeoDistance {
  from: Location;
  to: Location;
  distance: number;
}

class LocationIntelligenceAgent {
  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  private calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Calculate center point of a group of locations
   */
  private calculateCenterLocation(locations: Location[]): Location {
    if (locations.length === 0) {
      return { lat: 0, lng: 0 };
    }

    const sum = locations.reduce(
      (acc, loc) => ({
        lat: acc.lat + loc.lat,
        lng: acc.lng + loc.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / locations.length,
      lng: sum.lng / locations.length,
    };
  }

  /**
   * Calculate optimal route using nearest neighbor algorithm
   */
  async calculateOptimalRoute(
    startLocation: Location,
    farms: Farm[]
  ): Promise<Route> {
    if (farms.length === 0) {
      return {
        farms: [],
        totalDistance: 0,
        estimatedTime: 0,
        efficiency: 0,
      };
    }

    const visited = new Set<string>();
    const route: Farm[] = [];
    let currentLocation = startLocation;
    let totalDistance = 0;

    // Start with nearest farm
    let nearestFarm: Farm | null = null;
    let nearestDistance = Infinity;

    for (const farm of farms) {
      const distance = this.calculateDistance(
        currentLocation.lat,
        currentLocation.lng,
        farm.location.lat,
        farm.location.lng
      );

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestFarm = farm;
      }
    }

    if (nearestFarm) {
      route.push(nearestFarm);
      visited.add(nearestFarm.id);
      totalDistance += nearestDistance;
      currentLocation = nearestFarm.location;
    }

    // Use nearest neighbor to build route
    while (visited.size < farms.length) {
      nearestFarm = null;
      nearestDistance = Infinity;

      for (const farm of farms) {
        if (!visited.has(farm.id)) {
          const distance = this.calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            farm.location.lat,
            farm.location.lng
          );

          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestFarm = farm;
          }
        }
      }

      if (nearestFarm) {
        route.push(nearestFarm);
        visited.add(nearestFarm.id);
        totalDistance += nearestDistance;
        currentLocation = nearestFarm.location;
      } else {
        break;
      }
    }

    // Estimate time (30 min per stop + 2 min per mile)
    const estimatedTime = route.length * 30 + totalDistance * 2;

    // Efficiency score (0-100)
    const directDistance = this.calculateDistance(
      startLocation.lat,
      startLocation.lng,
      currentLocation.lat,
      currentLocation.lng
    );
    const efficiency =
      directDistance > 0
        ? Math.max(0, Math.min(100, (directDistance / totalDistance) * 100))
        : 0;

    return {
      farms: route,
      totalDistance,
      estimatedTime,
      efficiency,
    };
  }

  /**
   * Identify clusters of farms based on proximity
   */
  async identifyFarmClusters(
    farms: Farm[],
    clusterRadius: number = 5
  ): Promise<FarmCluster[]> {
    const clusters: FarmCluster[] = [];
    const visited = new Set<string>();

    for (const farm of farms) {
      if (visited.has(farm.id)) continue;

      // Find all farms within radius
      const clusterFarms = farms.filter((f) => {
        if (visited.has(f.id)) return false;

        const distance = this.calculateDistance(
          farm.location.lat,
          farm.location.lng,
          f.location.lat,
          f.location.lng
        );

        return distance <= clusterRadius;
      });

      // Mark as visited
      clusterFarms.forEach((f) => visited.add(f.id));

      // Calculate cluster properties
      const centerLocation = this.calculateCenterLocation(
        clusterFarms.map((f) => f.location)
      );
      const maxDistance = Math.max(
        ...clusterFarms.map((f) =>
          this.calculateDistance(
            centerLocation.lat,
            centerLocation.lng,
            f.location.lat,
            f.location.lng
          )
        )
      );
      const density = clusterFarms.length;

      clusters.push({
        clusterId: `cluster_${clusters.length + 1}`,
        farms: clusterFarms,
        centerLocation,
        radius: maxDistance,
        density,
      });
    }

    // Sort by density
    clusters.sort((a, b) => b.density - a.density);

    return clusters;
  }

  /**
   * Find nearby farms within a specified radius
   */
  async findNearbyFarms(
    center: Location,
    farms: Farm[],
    radiusMiles: number = 10
  ): Promise<Farm[]> {
    return farms
      .filter((farm) => {
        const distance = this.calculateDistance(
          center.lat,
          center.lng,
          farm.location.lat,
          farm.location.lng
        );
        return distance <= radiusMiles;
      })
      .sort((a, b) => {
        const distA = this.calculateDistance(
          center.lat,
          center.lng,
          a.location.lat,
          a.location.lng
        );
        const distB = this.calculateDistance(
          center.lat,
          center.lng,
          b.location.lat,
          b.location.lng
        );
        return distA - distB;
      })
      .map((farm) => {
        farm.distance = this.calculateDistance(
          center.lat,
          center.lng,
          farm.location.lat,
          farm.location.lng
        );
        return farm;
      });
  }

  /**
   * Get geographic statistics for farms
   */
  async getGeoStatistics(farms: Farm[]): Promise<{
    center: Location;
    totalSpread: number;
    averageDistance: number;
    clusterCount: number;
    farmCount: number;
  }> {
    if (farms.length === 0) {
      return {
        center: { lat: 0, lng: 0 },
        totalSpread: 0,
        averageDistance: 0,
        clusterCount: 0,
        farmCount: 0,
      };
    }

    const center = this.calculateCenterLocation(farms.map((f) => f.location));

    // Calculate total spread
    let totalSpread = 0;
    for (const farm of farms) {
      const distance = this.calculateDistance(
        center.lat,
        center.lng,
        farm.location.lat,
        farm.location.lng
      );
      totalSpread = Math.max(totalSpread, distance);
    }

    // Calculate average distance from center
    const distances = farms.map((farm) =>
      this.calculateDistance(
        center.lat,
        center.lng,
        farm.location.lat,
        farm.location.lng
      )
    );
    const averageDistance = distances.reduce((a, b) => a + b, 0) / farms.length;

    // Get cluster count
    const clusters = await this.identifyFarmClusters(farms);

    return {
      center,
      totalSpread,
      averageDistance,
      clusterCount: clusters.length,
      farmCount: farms.length,
    };
  }

  /**
   * Calculate service area for a given location
   */
  async calculateServiceArea(
    center: Location,
    farms: Farm[],
    maxDistance: number = 25
  ): Promise<{
    location: Location;
    radius: number;
    farmsInArea: number;
    coverage: number;
  }> {
    const farmsInArea = await this.findNearbyFarms(center, farms, maxDistance);

    return {
      location: center,
      radius: maxDistance,
      farmsInArea: farmsInArea.length,
      coverage: farms.length > 0 ? (farmsInArea.length / farms.length) * 100 : 0,
    };
  }

  /**
   * Find farms along a path
   */
  async findFarmsAlongPath(
    startLocation: Location,
    endLocation: Location,
    farms: Farm[],
    bufferDistance: number = 2
  ): Promise<Farm[]> {
    // Create a rectangular buffer around the path
    const minLat = Math.min(startLocation.lat, endLocation.lat) - bufferDistance / 69;
    const maxLat = Math.max(startLocation.lat, endLocation.lat) + bufferDistance / 69;
    const minLng = Math.min(startLocation.lng, endLocation.lng) - bufferDistance / 55;
    const maxLng = Math.max(startLocation.lng, endLocation.lng) + bufferDistance / 55;

    return farms.filter(
      (farm) =>
        farm.location.lat >= minLat &&
        farm.location.lat <= maxLat &&
        farm.location.lng >= minLng &&
        farm.location.lng <= maxLng
    );
  }

  /**
   * Calculate bounding box for farms
   */
  async calculateBoundingBox(
    farms: Farm[]
  ): Promise<{
    northEast: Location;
    southWest: Location;
    center: Location;
    width: number;
    height: number;
  }> {
    if (farms.length === 0) {
      return {
        northEast: { lat: 0, lng: 0 },
        southWest: { lat: 0, lng: 0 },
        center: { lat: 0, lng: 0 },
        width: 0,
        height: 0,
      };
    }

    let minLat = farms[0].location.lat;
    let maxLat = farms[0].location.lat;
    let minLng = farms[0].location.lng;
    let maxLng = farms[0].location.lng;

    for (const farm of farms) {
      minLat = Math.min(minLat, farm.location.lat);
      maxLat = Math.max(maxLat, farm.location.lat);
      minLng = Math.min(minLng, farm.location.lng);
      maxLng = Math.max(maxLng, farm.location.lng);
    }

    const center: Location = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    };

    // Rough conversion to miles (1 degree lat = ~69 miles, 1 degree lng = ~55 miles at Florida latitude)
    const width = (maxLng - minLng) * 55;
    const height = (maxLat - minLat) * 69;

    return {
      northEast: { lat: maxLat, lng: maxLng },
      southWest: { lat: minLat, lng: minLng },
      center,
      width,
      height,
    };
  }
}

export default new LocationIntelligenceAgent();
